import { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Plus, Download, Eye, FileText, Printer, Settings, Trash2, ClipboardList, Truck, AlertTriangle, CheckCircle, MoreVertical, Filter, ArrowUpDown, CalendarDays, MessageCircle } from 'lucide-react';
import { FilterDropdown } from '../../components/ui/FilterDropdown';
import { Modal } from '../../components/ui/Modal';
import { Input } from '../../components/ui/Input';
import { Toggle } from '../../components/ui/Toggle';
import { ordersApi } from '../../api/endpoints/orders';
import { shippingApi } from '../../api/endpoints/shipping';
import { settingsApi } from '../../api/endpoints/settings';
import { categoriesApi } from '../../api/endpoints/categories';
import { productsApi } from '../../api/endpoints/products';
import { DataTable, type Column } from '../../components/ui/DataTable';
import { Button } from '../../components/ui/Button';
import { SearchInput } from '../../components/ui/SearchInput';
import { Select } from '../../components/ui/Select';
import { StatusBadge } from '../../components/ui/StatusBadge';
import { CurrencyDisplay } from '../../components/ui/CurrencyDisplay';
import { LoadingSpinner } from '../../components/ui/LoadingSpinner';
import { Badge } from '../../components/ui/Badge';
import { PermissionGate } from '../../components/ui/PermissionGate';
import { useToast } from '../../components/ui/Alert';
import { QuotationModal } from '../../components/ui/QuotationModal';
import { useDebounce } from '../../hooks/useDebounce';
import { usePagination } from '../../hooks/usePagination';
import { formatDate } from '../../lib/formatters';
import { useStore } from '../../contexts/StoreContext';
import type { Order, OrderStatus, AutomationRule, AutomationConditionType } from '../../types/order';
import { ORDER_STATUSES, DELIVERY_STATUSES, DELIVERY_STATUS_CLASSES, PAYMENT_STATUSES, SHIPPING_STATUSES } from '../../lib/constants';
import { OrderDetailsSlideOver } from './OrderDetailsSlideOver';
import { OrderCreateModal } from './OrderCreateModal';
import { CreateShippingLabelModal } from './CreateShippingLabelModal';

// eslint-disable-next-line @typescript-eslint/no-unused-vars

export function OrderList() {
  const { storeId } = useParams<{ storeId: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { toast } = useToast();
  const { currentStore } = useStore();
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search);
  const [statusFilter, setStatusFilter] = useState('');
  const [paymentFilter, setPaymentFilter] = useState('');
  const [shippingFilter, setShippingFilter] = useState('');
  const [sourceFilter, setSourceFilter] = useState('');
  const [sortDir, setSortDir] = useState<'desc' | 'asc'>('desc');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const { page, limit, goToPage } = usePagination({
    resetDeps: [debouncedSearch, statusFilter, paymentFilter, shippingFilter, sourceFilter, sortDir, startDate, endDate],
  });
  const [datePreset, setDatePreset] = useState('all');
  const [showCustomDate, setShowCustomDate] = useState(false);

  // Overlay states - replaces page navigation for details, create, and label flows
  const [viewOrderId, setViewOrderId] = useState<string | null>(null);
  const [showCreateOrder, setShowCreateOrder] = useState(false);
  const [labelOrderId, setLabelOrderId] = useState<string | null>(null);

  // Selection & bulk actions
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [bulkPrinting, setBulkPrinting] = useState(false);
  const [bulkLabelPrinting, setBulkLabelPrinting] = useState(false);

  // Bulk label creation modal
  const [showBulkLabelModal, setShowBulkLabelModal] = useState(false);
  const [bulkLabelStep, setBulkLabelStep] = useState<1 | 2>(1); // 1 = summary, 2 = create form
  const [bulkWeight, setBulkWeight] = useState('1.0');
  const [bulkCarrier, setBulkCarrier] = useState<number | null>(null);
  const [bulkCarriers, setBulkCarriers] = useState<Array<{
    id: number; title: string; title_arabic?: string; carrier_logo?: string;
    rate?: number; approximate_delivery_time_arabic?: string; warning?: string;
  }>>([]);
  const [bulkCarriersLoading, setBulkCarriersLoading] = useState(false);
  const [bulkLabelProgress, setBulkLabelProgress] = useState<{ current: number; total: number; done: boolean } | null>(null);

  // Quotation modal
  const [showQuotation, setShowQuotation] = useState(false);

  // Export modal
  const [showExport, setShowExport] = useState(false);
  const [exportStart, setExportStart] = useState('');
  const [exportEnd, setExportEnd] = useState('');

  // Auto-status settings modal
  const [showAutoStatus, setShowAutoStatus] = useState(false);
  const [headerMenuOpen, setHeaderMenuOpen] = useState(false);
  const [autoRules, setAutoRules] = useState<AutomationRule[]>([]);
  const [autoEnabled, setAutoEnabled] = useState(false);
  const [autoLoading, setAutoLoading] = useState(false);
  const [showAddRule, setShowAddRule] = useState(false);
  const [newRule, setNewRule] = useState<{
    name: string;
    conditionType: AutomationConditionType;
    conditionValue: string;
    conditionValues: string[];
    actionValue: string;
    priority: number;
  }>({ name: '', conditionType: 'product_type', conditionValue: 'digital', conditionValues: [], actionValue: 'confirmed', priority: 0 });

  const statusMutation = useMutation({
    mutationFn: ({ orderId, data }: { orderId: string; data: { status?: OrderStatus; delivery?: string } }) =>
      ordersApi.update(storeId!, orderId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['orders', storeId] });
      toast('success', t('common.success'));
    },
    onError: () => toast('error', t('common.error')),
  });

  const handleInvoiceDownload = async (orderId: string) => {
    try {
      const blob = await ordersApi.downloadInvoice(storeId!, orderId);
      if (navigator.share && /Mobi|Android/i.test(navigator.userAgent)) {
        try {
          const file = new File([blob], `invoice-${orderId}.pdf`, { type: 'application/pdf' });
          await navigator.share({ files: [file], title: t('orders.downloadInvoice', 'Invoice') });
          return;
        } catch {
          // Share cancelled or failed - fall back to download
        }
      }
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `invoice-${orderId}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast('error', t('common.error'));
    }
  };

  // Unseen count
  const { data: unseenCount } = useQuery({
    queryKey: ['orders-unseen', storeId],
    queryFn: () => ordersApi.unseenCount(storeId!),
    enabled: !!storeId,
    refetchInterval: 30000,
  });

  const { data, isLoading } = useQuery({
    queryKey: ['orders', storeId, { page, limit, search: debouncedSearch, delivery: statusFilter, paymentStatus: paymentFilter, shippingStatus: shippingFilter, source: sourceFilter, sort: sortDir, startDate, endDate }],
    queryFn: () =>
      ordersApi.list(storeId!, {
        page,
        limit,
        search: debouncedSearch || undefined,
        delivery: statusFilter || undefined,
        paymentStatus: paymentFilter || undefined,
        shippingStatus: shippingFilter || undefined,
        source: sourceFilter || undefined,
        sort: sortDir !== 'desc' ? sortDir : undefined,
        startDate: startDate || undefined,
        endDate: endDate || undefined,
      }),
    enabled: !!storeId,
  });

  const orders = data?.orders ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.ceil(total / limit);

  // Fetch counts per delivery status for tabs
  const deliveryStatusTabs: Array<{ value: string; label: string }> = [
    { value: '', label: t('common.all', 'All') },
    ...DELIVERY_STATUSES.map((s) => ({ value: s, label: t(`orders.${s}`, s) })),
  ];

  const statusCountQueries = useQuery({
    queryKey: ['orders-status-counts', storeId, { search: debouncedSearch, paymentStatus: paymentFilter, shippingStatus: shippingFilter, source: sourceFilter, startDate, endDate }],
    queryFn: () => ordersApi.statusCounts(storeId!, {
      search: debouncedSearch || undefined,
      paymentStatus: paymentFilter || undefined,
      shippingStatus: shippingFilter || undefined,
      source: sourceFilter || undefined,
      startDate: startDate || undefined,
      endDate: endDate || undefined,
    }),
    enabled: !!storeId,
    staleTime: 30000,
  });

  const rawCounts = statusCountQueries.data;
  const statusCounts = rawCounts ? {
    '': rawCounts.all ?? 0,
    ...Object.fromEntries(DELIVERY_STATUSES.map(s => [s, rawCounts[s] ?? 0]))
  } as Record<string, number> : undefined;

  const handleExport = async () => {
    try {
      const blob = await ordersApi.exportCsv(storeId!, {
        delivery: statusFilter || undefined,
        source: sourceFilter || undefined,
        search: debouncedSearch || undefined,
        startDate: exportStart || startDate || undefined,
        endDate: exportEnd || endDate || undefined,
        sort: sortDir,
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `orders-${exportStart || startDate || 'all'}-${exportEnd || endDate || 'now'}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast('success', t('common.success'));
      setShowExport(false);
    } catch {
      toast('error', t('common.error'));
    }
  };

  const handleBulkPrint = async () => {
    if (selectedIds.size === 0) return;
    setBulkPrinting(true);
    try {
      for (const id of selectedIds) {
        const blob = await ordersApi.downloadInvoice(storeId!, id);
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `invoice-${id}.pdf`;
        a.click();
        URL.revokeObjectURL(url);
      }
      toast('success', t('orders.bulkPrintSuccess', `Downloaded ${selectedIds.size} invoices`));
    } catch {
      toast('error', t('common.error'));
    } finally {
      setBulkPrinting(false);
    }
  };

  // Computed lists for bulk label modal
  const bulkLabelOrders = useMemo(() => {
    const selected = orders.filter((o: any) => selectedIds.has(o._id));
    return {
      withLabels: selected.filter((o: any) => o.shippingLabel),
      withoutLabels: selected.filter((o: any) => !o.shippingLabel),
      total: selected.length,
    };
  }, [orders, selectedIds]);

  const handleBulkLabelPrint = async () => {
    if (selectedIds.size === 0) return;

    const selectedOrders = orders.filter((o: any) => selectedIds.has(o._id));
    const withLabels = selectedOrders.filter((o: any) => o.shippingLabel);
    const withoutLabels = selectedOrders.filter((o: any) => !o.shippingLabel);

    if (withLabels.length === 0 && withoutLabels.length === 0) return;

    // If ALL selected orders already have labels, download directly
    if (withoutLabels.length === 0) {
      setBulkLabelPrinting(true);
      try {
        for (const order of withLabels) {
          await shippingApi.downloadLabel(storeId!, order._id);
        }
        toast('success', t('orders.bulkLabelSuccess', `Downloaded ${withLabels.length} shipping label(s)`));
      } catch {
        toast('error', t('common.error'));
      } finally {
        setBulkLabelPrinting(false);
      }
      return;
    }

    // Some orders are missing labels -- open the summary modal
    setBulkLabelStep(1);
    setBulkWeight('1.0');
    setBulkCarrier(null);
    setBulkCarriers([]);
    setBulkLabelProgress(null);
    setShowBulkLabelModal(true);
  };

  const handleBulkDownloadReadyOnly = async () => {
    setShowBulkLabelModal(false);
    if (bulkLabelOrders.withLabels.length === 0) {
      toast('warning', t('orders.noLabelsAvailable', 'None of the selected orders have shipping labels.'));
      return;
    }
    setBulkLabelPrinting(true);
    try {
      for (const order of bulkLabelOrders.withLabels) {
        await shippingApi.downloadLabel(storeId!, order._id);
      }
      toast('success', t('orders.bulkLabelSuccess', `Downloaded ${bulkLabelOrders.withLabels.length} shipping label(s)`));
    } catch {
      toast('error', t('common.error'));
    } finally {
      setBulkLabelPrinting(false);
    }
  };

  const handleBulkCreateMissing = async () => {
    // Move to step 2 -- fetch carriers
    setBulkLabelStep(2);
    setBulkCarriersLoading(true);
    try {
      const firstOrder = bulkLabelOrders.withoutLabels[0];
      const result = await shippingApi.listAvailableCarriers(storeId!, { orderId: firstOrder?._id });
      setBulkCarriers(result.companies || []);
    } catch {
      toast('error', t('orders.carriersFetchError', 'Failed to load shipping carriers'));
      setBulkCarriers([]);
    } finally {
      setBulkCarriersLoading(false);
    }
  };

  const handleBulkCreateLabels = async () => {
    if (!bulkCarrier) return;
    const ordersToCreate = bulkLabelOrders.withoutLabels;
    const totalToCreate = ordersToCreate.length;

    setBulkLabelProgress({ current: 0, total: totalToCreate, done: false });

    let successCount = 0;
    let failCount = 0;

    for (let i = 0; i < ordersToCreate.length; i++) {
      setBulkLabelProgress({ current: i + 1, total: totalToCreate, done: false });
      try {
        await shippingApi.createLabel(storeId!, {
          orderId: ordersToCreate[i]._id,
          carrierId: String(bulkCarrier),
        });
        successCount++;
      } catch {
        failCount++;
      }
    }

    setBulkLabelProgress({ current: totalToCreate, total: totalToCreate, done: true });

    // Invalidate orders to refresh shippingLabel fields
    queryClient.invalidateQueries({ queryKey: ['orders', storeId] });

    if (failCount > 0) {
      toast('warning', t('orders.bulkLabelPartial', `Created ${successCount} labels, ${failCount} failed`));
    } else {
      toast('success', t('orders.bulkLabelCreated', `Created ${successCount} shipping label(s)`));
    }

    // Now download ALL labels (both previously existing + newly created)
    setShowBulkLabelModal(false);
    setBulkLabelPrinting(true);
    try {
      // Re-fetch orders to get updated shippingLabel data
      const refreshed = await ordersApi.list(storeId!, { page: 1, limit: 200 });
      const allSelected = refreshed.orders.filter((o: Order) => selectedIds.has(o._id));
      const downloadable = allSelected.filter((o: Order) => o.shippingLabel);

      for (const order of downloadable) {
        await shippingApi.downloadLabel(storeId!, order._id);
      }
      toast('success', t('orders.bulkLabelSuccess', `Downloaded ${downloadable.length} shipping label(s)`));
    } catch {
      toast('error', t('orders.bulkDownloadError', 'Labels were created but download failed. Try downloading again.'));
    } finally {
      setBulkLabelPrinting(false);
      setBulkLabelProgress(null);
    }
  };

  // Fetch categories & products for automation rule configuration
  const { data: autoCategoriesData } = useQuery({
    queryKey: ['categories-mini', storeId],
    queryFn: () => categoriesApi.list(storeId!),
    enabled: !!storeId && (showAddRule || showAutoStatus),
  });

  const { data: autoProductsData } = useQuery({
    queryKey: ['products-mini', storeId],
    queryFn: () => productsApi.list(storeId!, { limit: 200 }),
    enabled: !!storeId && (showAddRule || showAutoStatus),
  });

  const openAutoStatus = async () => {
    setShowAutoStatus(true);
    setAutoLoading(true);
    try {
      const result = await settingsApi.getAutomation(storeId!);
      // Normalise legacy rules that may lack priority / values fields
      const rules: AutomationRule[] = (result.automation?.rules ?? []).map((r: any) => ({
        id: r.id,
        name: r.name || '',
        condition: {
          type: r.condition?.type ?? r.applyTo ?? 'any',
          value: r.condition?.value ?? '',
          values: r.condition?.values ?? r.categories ?? r.products ?? [],
        },
        action: {
          type: r.action?.type ?? 'set_delivery',
          value: r.action?.value ?? r.status ?? 'confirmed',
        },
        priority: r.priority ?? 0,
        enabled: r.enabled ?? true,
      }));
      setAutoRules(rules);
      setAutoEnabled(result.automation?.enabled ?? false);
    } catch {
      setAutoRules([]);
      setAutoEnabled(false);
    } finally {
      setAutoLoading(false);
    }
  };

  const saveAutoStatus = async () => {
    try {
      await settingsApi.updateAutomation(storeId!, { rules: autoRules, enabled: autoEnabled });
      toast('success', t('common.success'));
      setShowAutoStatus(false);
    } catch {
      toast('error', t('common.error'));
    }
  };

  const toggleAutoRule = (index: number) => {
    setAutoRules(prev => prev.map((r, i) => i === index ? { ...r, enabled: !r.enabled } : r));
  };

  const removeAutoRule = (id: string) => {
    setAutoRules(prev => prev.filter(r => r.id !== id));
  };

  const resetNewRule = () => {
    setNewRule({ name: '', conditionType: 'product_type', conditionValue: 'digital', conditionValues: [], actionValue: 'confirmed', priority: autoRules.length });
  };

  const addAutoRule = () => {
    if (!newRule.name.trim()) return;
    setAutoRules(prev => [...prev, {
      id: `rule-${Date.now()}`,
      name: newRule.name,
      condition: {
        type: newRule.conditionType,
        value: newRule.conditionValue,
        values: newRule.conditionValues,
      },
      action: { type: 'set_delivery', value: newRule.actionValue },
      priority: newRule.priority,
      enabled: true,
    }]);
    setShowAddRule(false);
    resetNewRule();
  };

  const conditionLabels: Record<string, string> = {
    product_type: t('automation.productType', 'Product type is'),
    all_digital: t('automation.allDigital', 'All items are digital'),
    payment_cod: t('automation.paymentCod', 'Payment method is COD'),
    payment_method: t('automation.paymentMethod', 'Payment method is'),
    category: t('automation.category', 'Category is'),
    product: t('automation.product', 'Product is'),
    any: t('automation.anyOrder', 'Any order'),
  };

  const paymentMethodLabels: Record<string, string> = {
    bank_transfer: t('automation.bankTransfer', 'Bank Transfer'),
    moyasar: t('automation.moyasar', 'Moyasar'),
    clickpay: t('automation.clickpay', 'ClickPay'),
    paypal: t('automation.paypal', 'PayPal'),
    stcpay: t('automation.stcpay', 'STC Pay'),
  };

  const deliveryLabels: Record<string, string> = {
    confirming: t('automation.confirming', 'Awaiting confirmation'),
    confirmed: t('automation.confirmed', 'Confirmed'),
    delivering: t('automation.delivering', 'Delivering'),
    delivered: t('automation.delivered', 'Delivered'),
  };

  // Build lookup maps for readable rule descriptions
  const categoryMap = useMemo(() => {
    const map: Record<string, string> = {};
    (autoCategoriesData?.categories ?? []).forEach(c => { map[c._id] = c.name; });
    return map;
  }, [autoCategoriesData]);

  const productMap = useMemo(() => {
    const map: Record<string, string> = {};
    (autoProductsData?.products ?? []).forEach(p => { map[p._id] = p.title; });
    return map;
  }, [autoProductsData]);

  /** Build a human-readable description for a rule's condition */
  const describeCondition = (rule: AutomationRule): string => {
    const label = conditionLabels[rule.condition.type] || rule.condition.type;
    switch (rule.condition.type) {
      case 'product_type':
        return `${label} "${rule.condition.value}"`;
      case 'payment_method':
        return `${label} ${paymentMethodLabels[rule.condition.value] || rule.condition.value}`;
      case 'category': {
        const names = (rule.condition.values ?? []).map(id => categoryMap[id] || id).join(', ');
        return names ? `${label} ${names}` : label;
      }
      case 'product': {
        const names = (rule.condition.values ?? []).map(id => productMap[id] || id).join(', ');
        return names ? `${label} ${names}` : label;
      }
      default:
        return label;
    }
  };

  // Sort displayed rules by priority (higher first)
  const sortedAutoRules = useMemo(() => [...autoRules].sort((a, b) => (b.priority ?? 0) - (a.priority ?? 0)), [autoRules]);

  const columns: Column<Order>[] = [
    {
      key: 'orderNumber',
      header: t('orders.orderNumber'),
      sortable: true,
      hideOnMobile: true,
      render: (o) => (
        <div className="flex items-center gap-1.5">
          <span
            className="font-mono text-accent hover:underline cursor-pointer"
            onClick={(e) => { e.stopPropagation(); navigate(`/${storeId}/orders/${o._id}`); }}
          >
            #{o.orderNumber ?? o._id.slice(-6)}
          </span>
          {!o.seen && (
            <Badge variant="success" className="text-[10px] uppercase tracking-wide">{t('orders.new', 'New')}</Badge>
          )}
          {o.channel === 'manual' && (
            <Badge variant="warning" className="text-[10px]">{t('orders.manual', 'Manual')}</Badge>
          )}
          {o.channel === 'paylink' && (
            <Badge variant="info" className="text-[10px]">{t('orders.sourcePaylink', 'Pay Link')}</Badge>
          )}
          {o.channel === 'guest' && (
            <Badge variant="default" className="text-[10px]">{t('orders.guest', 'Guest')}</Badge>
          )}
          {o.paymentMethod === 'bankTransfer' && o.bankTransferStatus === 'pending' && (
            <Badge variant="info" className="text-[10px]">{t('orders.bankPending', 'تحويل بنكي')}</Badge>
          )}
        </div>
      ),
    },
    {
      key: 'customer',
      header: t('orders.customer'),
      render: (o) => (
        <div>
          {o.customer?.userId ? (
            <p
              className="text-sm hover:text-accent hover:underline cursor-pointer transition-colors"
              onClick={(e) => { e.stopPropagation(); navigate(`/${storeId}/customers/${o.customer!.userId}`); }}
            >
              {o.customer?.name || '-'}
            </p>
          ) : (
            <p className="text-sm">{o.customer?.name ?? '-'}</p>
          )}
          {/* Mobile: show total under name since total column is hidden */}
          <p className="text-xs text-text-muted md:hidden"><CurrencyDisplay amount={o.total} currency={o.currency || currentStore?.currency} /></p>
          {o.customer?.phone && (() => {
            const phoneStr = String(o.customer.phone);
            const waDigits = phoneStr.replace(/\D/g, '');
            // Outer <p> stays block so it inherits the cell's text-align
            // (right in RTL, left in LTR) — keeping phone aligned with the
            // name above. Inner inline-flex carries dir="ltr" so the +966...
            // and the WhatsApp icon render in reading order.
            return (
              <p className="text-xs text-text-muted hidden md:block tabular-nums">
                <span dir="ltr" className="inline-flex items-center gap-1.5">
                  <span>{phoneStr}</span>
                  {waDigits && (
                    <a
                      href={`https://wa.me/${waDigits}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e) => e.stopPropagation()}
                      title={t('customers.whatsapp', 'WhatsApp')}
                      className="inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-full text-[#25D366] hover:scale-110 transition-transform"
                    >
                      <MessageCircle className="h-3.5 w-3.5" />
                    </a>
                  )}
                </span>
              </p>
            );
          })()}
        </div>
      ),
    },
    {
      key: 'total',
      header: t('common.total'),
      sortable: true,
      hideOnMobile: true,
      render: (o) => (
        <CurrencyDisplay amount={o.total} currency={o.currency || currentStore?.currency} className="font-medium" />
      ),
    },
    {
      key: 'items',
      header: t('orders.items'),
      hideOnMobile: true,
      render: (o) => {
        const items = o.items ?? [];
        if (items.length === 0) return <span className="text-text-muted">-</span>;
        const fullTitle = items[0].title || items[0].variantTitle || '-';
        const words = fullTitle.split(/\s+/);
        const first = words.length > 2 ? words.slice(0, 2).join(' ') + '...' : fullTitle;
        const rest = items.length - 1;
        return (
          <div className="max-w-[140px]">
            <p className="text-sm" title={fullTitle}>{first}</p>
            {rest > 0 && <p className="text-xs text-text-muted">+{rest}</p>}
          </div>
        );
      },
    },
    {
      key: 'status',
      header: t('common.status'),
      hideOnMobile: true,
      render: (o) => {
        const value = o.delivery || o.status;
        const colorClasses = DELIVERY_STATUS_CLASSES[value] || 'bg-bg-tertiary text-text-primary border-border-default';
        // Editing delivery status is an orders.edit write; view-only staff get a
        // read-only badge instead of an interactive dropdown (the backend
        // enforces orders.edit too — this just stops the dead control + 403).
        return (
          <PermissionGate
            requires="orders.edit"
            fallback={<span className={`inline-block rounded-md border px-2 py-1 text-xs font-medium ${colorClasses}`}>{t(`orders.${value}`, value)}</span>}
          >
            <select
              value={value}
              onChange={(e) => statusMutation.mutate({ orderId: o._id, data: { delivery: e.target.value } })}
              className={`rounded-md border px-2 py-1 text-xs font-medium outline-none focus:border-accent cursor-pointer ${colorClasses}`}
            >
              {DELIVERY_STATUSES.map((s) => (
                <option key={s} value={s} className="bg-bg-primary text-text-primary">{t(`orders.${s}`, s)}</option>
              ))}
            </select>
          </PermissionGate>
        );
      },
    },
    {
      key: 'date',
      header: t('common.date'),
      sortable: true,
      hideOnMobile: true,
      render: (o) => <span className="text-text-secondary text-xs">{formatDate(o.createdAt, 'dd MMM, hh:mm a')}</span>,
    },
    {
      key: 'actions',
      header: '',
      className: 'w-auto text-end',
      render: (o) => (
        <div className="flex items-center justify-end gap-1">
          {o.shippingLabel ? (
            <button
              onClick={() => shippingApi.downloadLabel(storeId!, o._id)}
              className="hidden md:inline-flex rounded-lg p-1.5 text-text-muted hover:bg-bg-hover hover:text-accent"
              title={t('orders.downloadLabel', 'Download Label')}
            >
              <Truck className="h-4 w-4" />
            </button>
          ) : o.shippingAddress ? (
            <button
              onClick={() => setLabelOrderId(o._id)}
              className="hidden md:inline-flex rounded-lg p-1.5 text-text-muted hover:bg-bg-hover hover:text-warning"
              title={t('orders.createLabel', 'Create Label')}
            >
              <Truck className="h-4 w-4 opacity-50" />
            </button>
          ) : null}
          <button
            onClick={() => handleInvoiceDownload(o._id)}
            className="hidden md:inline-flex rounded-lg p-1.5 text-text-muted hover:bg-bg-hover hover:text-accent"
            title={t('orders.downloadInvoice', 'Invoice')}
          >
            <FileText className="h-4 w-4" />
          </button>
          <button
            onClick={() => setViewOrderId(o._id)}
            className="inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-medium bg-accent/10 text-accent hover:bg-accent/20 transition-colors"
          >
            <Eye className="h-3.5 w-3.5" />
            <span className="hidden md:inline">{t('orders.view', 'عرض')}</span>
          </button>
        </div>
      ),
    },
  ];

  return (
    <div>
      {/* Header */}
      <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="text-xl font-bold">{t('orders.title')}</h1>
            {total != null && (
              <span className="rounded-full bg-bg-tertiary px-2.5 py-0.5 text-xs font-medium text-text-secondary">
                {total}
              </span>
            )}
            {unseenCount != null && unseenCount > 0 && (
              <Badge variant="accent">{unseenCount} {t('orders.newOrders')}</Badge>
            )}
          </div>
          <p className="mt-1 text-sm text-text-secondary hidden sm:block">{t('orders.description', 'Manage and track store orders')}</p>
        </div>
        <div className="flex items-center gap-2">
          <PermissionGate requires="orders.edit">
            <Button onClick={() => setShowCreateOrder(true)}>
              <Plus className="h-4 w-4" />
              {t('orders.addOrder')}
            </Button>
          </PermissionGate>
          <div className="relative">
            <button
              onClick={() => setHeaderMenuOpen(!headerMenuOpen)}
              className="rounded-lg p-2 text-text-muted hover:bg-bg-hover hover:text-text-primary border border-border-default"
            >
              <MoreVertical className="h-4 w-4" />
            </button>
            {headerMenuOpen && (
              <>
                <div className="fixed inset-0 z-10" onClick={() => setHeaderMenuOpen(false)} />
                <div className="absolute end-0 top-full z-20 mt-1 w-48 rounded-lg border border-border-default bg-bg-secondary shadow-xl">
                  <button onClick={() => { setShowQuotation(true); setHeaderMenuOpen(false); }} className="flex w-full items-center gap-2 px-3 py-2.5 text-sm hover:bg-bg-hover">
                    <ClipboardList className="h-4 w-4" /> {t('products.generateQuotation', 'Quotation')}
                  </button>
                  <button onClick={() => { setExportStart(startDate); setExportEnd(endDate); setShowExport(true); setHeaderMenuOpen(false); }} className="flex w-full items-center gap-2 px-3 py-2.5 text-sm hover:bg-bg-hover">
                    <Download className="h-4 w-4" /> {t('orders.exportCsv')}
                  </button>
                  <button onClick={() => { openAutoStatus(); setHeaderMenuOpen(false); }} className="flex w-full items-center gap-2 px-3 py-2.5 text-sm hover:bg-bg-hover">
                    <Settings className="h-4 w-4" /> {t('orders.autoStatus', 'Auto Status')}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Delivery tabs */}
      <div className="mb-3 flex items-center gap-1 overflow-x-auto pb-0.5 scrollbar-none">
        {deliveryStatusTabs.map((tab) => (
          <button
            key={tab.value}
            onClick={() => { setStatusFilter(tab.value); goToPage(1); }}
            className={`shrink-0 rounded-full px-2.5 py-1 text-[11px] font-medium transition-colors ${
              statusFilter === tab.value
                ? 'bg-accent text-white'
                : 'text-text-secondary hover:bg-bg-hover'
            }`}
          >
            {tab.label}
            {statusCounts != null && statusCounts[tab.value] != null && (
              <span className="ms-0.5 opacity-70">{statusCounts[tab.value]}</span>
            )}
          </button>
        ))}
      </div>

      {/* Filters row */}
      <div className="mb-4 flex flex-wrap items-center gap-2">
        <SearchInput
          value={search}
          onChange={setSearch}
          placeholder={t('common.search') + '...'}
          className="w-full sm:w-52"
        />
        <FilterDropdown
          icon={Filter}
          options={[
            { value: '', label: t('orders.allSources', 'جميع المصادر') },
            { value: 'store', label: t('orders.sourceStore', 'المتجر') },
            { value: 'manual', label: t('orders.sourceManual', 'يدوي') },
          ]}
          value={sourceFilter}
          onChange={setSourceFilter}
        />
        <FilterDropdown
          icon={ArrowUpDown}
          options={[
            { value: 'desc', label: t('common.newestFirst', 'الأحدث أولاً') },
            { value: 'asc', label: t('common.oldestFirst', 'الأقدم أولاً') },
          ]}
          value={sortDir}
          onChange={(v) => setSortDir(v as 'desc' | 'asc')}
        />
        <FilterDropdown
          icon={CalendarDays}
          options={[
            { value: 'all', label: t('common.allTime', 'كل الأوقات') },
            { value: '1d', label: t('common.today', 'اليوم') },
            { value: '7d', label: '7 ' + t('common.days', 'أيام') },
            { value: '30d', label: '30 ' + t('common.days', 'يوم') },
            { value: '12m', label: '12 ' + t('common.months', 'شهر') },
            { value: 'custom', label: t('common.custom', 'مخصص') },
          ]}
          value={datePreset}
          onChange={(v) => {
            setDatePreset(v);
            const today = new Date().toISOString().split('T')[0];
            const daysAgo = (n: number) => { const d = new Date(); d.setDate(d.getDate() - n); return d.toISOString().split('T')[0]; };
            const monthsAgo = (n: number) => { const d = new Date(); d.setMonth(d.getMonth() - n); return d.toISOString().split('T')[0]; };
            if (v === 'all') { setStartDate(''); setEndDate(''); setShowCustomDate(false); }
            else if (v === '1d') { setStartDate(today); setEndDate(today); setShowCustomDate(false); }
            else if (v === '7d') { setStartDate(daysAgo(7)); setEndDate(today); setShowCustomDate(false); }
            else if (v === '30d') { setStartDate(daysAgo(30)); setEndDate(today); setShowCustomDate(false); }
            else if (v === '12m') { setStartDate(monthsAgo(12)); setEndDate(today); setShowCustomDate(false); }
            else if (v === 'custom') { setShowCustomDate(true); }
          }}
        />
        {showCustomDate && (
          <div className="flex items-center gap-1.5">
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="rounded-lg border border-border-default bg-bg-tertiary px-2 py-1.5 text-xs text-text-primary outline-none focus:border-accent"
            />
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="rounded-lg border border-border-default bg-bg-tertiary px-2 py-1.5 text-xs text-text-primary outline-none focus:border-accent"
            />
          </div>
        )}
      </div>

      {/* Bulk actions */}
      {selectedIds.size > 0 && (
        <div className="mb-3 flex items-center gap-3 rounded-lg border border-accent/30 bg-accent/5 px-4 py-2.5">
          <span className="text-sm font-medium">{selectedIds.size} {t('common.selected', 'selected')}</span>
          <Button variant="secondary" size="sm" onClick={handleBulkPrint} isLoading={bulkPrinting}>
            <Printer className="h-3.5 w-3.5" />
            {t('orders.bulkPrint', 'Print Invoices')}
          </Button>
          <Button variant="secondary" size="sm" onClick={handleBulkLabelPrint} isLoading={bulkLabelPrinting}>
            <Printer className="h-3.5 w-3.5" />
            {t('orders.bulkPrintLabels', 'Print Labels')}
          </Button>
          <Button variant="secondary" size="sm" onClick={() => setSelectedIds(new Set())}>
            {t('common.clearSelection', 'Clear')}
          </Button>
        </div>
      )}

      {/* Table */}
      <div className="rounded-xl border border-border-default bg-bg-secondary shadow-sm overflow-hidden">
        <DataTable
          columns={columns}
          data={orders}
          isLoading={isLoading}
          keyExtractor={(o) => o._id}
          emptyTitle={t('common.noResults')}
          emptyDescription={t('common.tryDifferentSearch')}
          selectable
          selectedIds={selectedIds}
          onSelectionChange={setSelectedIds}
          page={page}
          totalPages={totalPages}
          total={total}
          limit={limit}
          onPageChange={goToPage}
        />
      </div>

      {/* Export Modal */}
      <Modal
        isOpen={showExport}
        onClose={() => setShowExport(false)}
        title={t('orders.exportCsv')}
        footer={
          <>
            <Button variant="secondary" onClick={() => setShowExport(false)}>{t('common.cancel')}</Button>
            <Button onClick={handleExport}>
              <Download className="h-4 w-4" />
              {t('orders.exportCsv')}
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          {/* Show active filters summary */}
          <div className="rounded-lg bg-bg-tertiary p-3 space-y-1.5">
            <p className="text-xs font-medium text-text-secondary">{t('orders.exportFilters', 'Active Filters')}</p>
            <div className="flex flex-wrap gap-1.5">
              {statusFilter && <Badge>{deliveryStatusTabs.find(t => t.value === statusFilter)?.label || statusFilter}</Badge>}
              {sourceFilter && <Badge>{sourceFilter === 'store' ? t('orders.sourceStore', 'المتجر') : t('orders.sourceManual', 'يدوي')}</Badge>}
              {debouncedSearch && <Badge>{t('common.search')}: {debouncedSearch}</Badge>}
              {!statusFilter && !sourceFilter && !debouncedSearch && <span className="text-xs text-text-muted">{t('orders.noFilters', 'No filters - all orders')}</span>}
            </div>
          </div>

          {/* Date range - pre-filled from current filters */}
          <div className="grid gap-4 sm:grid-cols-2">
            <Input
              label={t('common.startDate', 'Start Date')}
              type="date"
              value={exportStart}
              onChange={(e) => setExportStart(e.target.value)}
            />
            <Input
              label={t('common.endDate', 'End Date')}
              type="date"
              value={exportEnd}
              onChange={(e) => setExportEnd(e.target.value)}
            />
          </div>
          <p className="text-xs text-text-muted">{t('orders.exportHintFilters', 'Exports orders matching the filters shown above. Adjust dates if needed.')}</p>
        </div>
      </Modal>

      {/* Auto-Status Settings Modal */}
      <Modal
        isOpen={showAutoStatus}
        onClose={() => setShowAutoStatus(false)}
        title={t('orders.autoStatus', 'Order Automation')}
        size="lg"
        footer={
          <>
            <Button variant="secondary" onClick={() => setShowAutoStatus(false)}>{t('common.cancel')}</Button>
            <Button onClick={saveAutoStatus}>{t('common.save')}</Button>
          </>
        }
      >
        {autoLoading ? (
          <p className="text-sm text-text-muted text-center py-6">{t('common.loading', 'Loading...')}</p>
        ) : (
          <div className="space-y-4">
            <Toggle
              label={t('orders.enableAutoStatus', 'Enable automatic status transitions')}
              checked={autoEnabled}
              onChange={setAutoEnabled}
              description={t('orders.autoStatusDesc', 'Automatically change order delivery status based on conditions when new orders come in. Rules with higher priority numbers run first.')}
            />

            <div className="flex items-center justify-between">
              <h3 className="text-sm font-semibold">{t('automation.rules', 'Rules')}</h3>
              <Button size="sm" onClick={() => { resetNewRule(); setShowAddRule(true); }} disabled={!autoEnabled}>
                <Plus className="h-4 w-4" />
                {t('automation.addRule', 'Add Rule')}
              </Button>
            </div>

            {sortedAutoRules.length === 0 ? (
              <p className="text-sm text-text-muted">{t('orders.noAutoRules', 'No automation rules configured.')}</p>
            ) : (
              <div className="space-y-2">
                {sortedAutoRules.map((rule) => {
                  const realIndex = autoRules.findIndex(r => r.id === rule.id);
                  return (
                    <div key={rule.id} className={`flex items-center gap-3 rounded-lg border p-3 ${rule.enabled && autoEnabled ? 'border-border-default bg-bg-tertiary' : 'border-border-default/50 bg-bg-tertiary/50 opacity-60'}`}>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <p className="text-sm font-medium">{rule.name || t('automation.untitled', 'Untitled rule')}</p>
                          <span className="shrink-0 rounded bg-bg-secondary px-1.5 py-0.5 text-[10px] font-medium text-text-muted">
                            {t('automation.priority', 'Priority')} {rule.priority ?? 0}
                          </span>
                        </div>
                        <p className="text-xs text-text-muted mt-0.5">
                          {describeCondition(rule)}
                          {' \u2192 '}
                          {deliveryLabels[rule.action.value] || rule.action.value}
                        </p>
                      </div>
                      <Toggle
                        checked={rule.enabled}
                        onChange={() => toggleAutoRule(realIndex)}
                      />
                      <button
                        onClick={() => removeAutoRule(rule.id)}
                        className="rounded-lg p-1.5 text-text-muted hover:bg-danger/10 hover:text-danger transition-colors"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </Modal>

      {/* Add Automation Rule Modal */}
      <Modal
        isOpen={showAddRule}
        onClose={() => setShowAddRule(false)}
        title={t('automation.addRule', 'Add Rule')}
        footer={
          <>
            <Button variant="secondary" onClick={() => setShowAddRule(false)}>{t('common.cancel')}</Button>
            <Button onClick={addAutoRule} disabled={!newRule.name.trim()}>{t('common.save')}</Button>
          </>
        }
      >
        <div className="space-y-4">
          <Input
            label={t('automation.ruleName', 'Rule name')}
            required
            placeholder={t('automation.ruleNamePlaceholder', 'e.g. Auto-confirm digital orders')}
            value={newRule.name}
            onChange={(e) => setNewRule({ ...newRule, name: e.target.value })}
          />
          <Select
            label={t('automation.condition', 'When (condition)')}
            options={[
              { value: 'any', label: t('automation.anyOrder', 'Any order') },
              { value: 'category', label: t('automation.category', 'Specific categories') },
              { value: 'product', label: t('automation.product', 'Specific products') },
              { value: 'payment_cod', label: t('automation.paymentCod', 'Payment method is COD') },
              { value: 'payment_method', label: t('automation.paymentMethod', 'Specific payment method') },
              { value: 'product_type', label: t('automation.productType', 'Product type is') },
              { value: 'all_digital', label: t('automation.allDigital', 'All items are digital') },
            ]}
            value={newRule.conditionType}
            onChange={(e) => setNewRule({ ...newRule, conditionType: e.target.value as AutomationConditionType, conditionValue: '', conditionValues: [] })}
          />

          {/* Product type sub-select */}
          {newRule.conditionType === 'product_type' && (
            <Select
              label={t('automation.productTypeValue', 'Product type')}
              options={[
                { value: 'digital', label: t('products.digital', 'Digital') },
                { value: 'service', label: t('products.service', 'Service') },
                { value: 'physical', label: t('products.physical', 'Physical') },
              ]}
              value={newRule.conditionValue}
              onChange={(e) => setNewRule({ ...newRule, conditionValue: e.target.value })}
            />
          )}

          {/* Payment method sub-select */}
          {newRule.conditionType === 'payment_method' && (
            <Select
              label={t('automation.paymentMethodValue', 'Payment method')}
              options={[
                { value: 'bank_transfer', label: t('automation.bankTransfer', 'Bank Transfer') },
                { value: 'moyasar', label: t('automation.moyasar', 'Moyasar') },
                { value: 'clickpay', label: t('automation.clickpay', 'ClickPay') },
                { value: 'paypal', label: t('automation.paypal', 'PayPal') },
                { value: 'stcpay', label: t('automation.stcpay', 'STC Pay') },
              ]}
              value={newRule.conditionValue}
              onChange={(e) => setNewRule({ ...newRule, conditionValue: e.target.value })}
            />
          )}

          {/* Category multi-select pills */}
          {newRule.conditionType === 'category' && (
            <div>
              <label className="mb-1 block text-sm font-medium text-text-secondary">
                {t('automation.selectCategories', 'Select categories')}
              </label>
              <div className="flex flex-wrap gap-2 rounded-lg border border-border-default bg-bg-secondary p-2 min-h-[40px]">
                {(autoCategoriesData?.categories ?? []).map((cat) => {
                  const selected = newRule.conditionValues.includes(cat._id);
                  return (
                    <button
                      key={cat._id}
                      type="button"
                      onClick={() => setNewRule(prev => ({
                        ...prev,
                        conditionValues: selected
                          ? prev.conditionValues.filter(id => id !== cat._id)
                          : [...prev.conditionValues, cat._id],
                      }))}
                      className={`rounded-md px-2.5 py-1 text-xs font-medium transition-colors ${
                        selected
                          ? 'bg-accent text-white'
                          : 'bg-bg-tertiary text-text-secondary hover:bg-bg-hover'
                      }`}
                    >
                      {cat.name}
                    </button>
                  );
                })}
                {(autoCategoriesData?.categories ?? []).length === 0 && (
                  <span className="text-xs text-text-muted">{t('common.noResults')}</span>
                )}
              </div>
            </div>
          )}

          {/* Product multi-select pills */}
          {newRule.conditionType === 'product' && (
            <div>
              <label className="mb-1 block text-sm font-medium text-text-secondary">
                {t('automation.selectProducts', 'Select products')}
              </label>
              <div className="flex flex-wrap gap-2 rounded-lg border border-border-default bg-bg-secondary p-2 max-h-48 overflow-y-auto min-h-[40px]">
                {(autoProductsData?.products ?? []).map((prod) => {
                  const selected = newRule.conditionValues.includes(prod._id);
                  return (
                    <button
                      key={prod._id}
                      type="button"
                      onClick={() => setNewRule(prev => ({
                        ...prev,
                        conditionValues: selected
                          ? prev.conditionValues.filter(id => id !== prod._id)
                          : [...prev.conditionValues, prod._id],
                      }))}
                      className={`rounded-md px-2.5 py-1 text-xs font-medium transition-colors ${
                        selected
                          ? 'bg-accent text-white'
                          : 'bg-bg-tertiary text-text-secondary hover:bg-bg-hover'
                      }`}
                    >
                      {prod.title}
                    </button>
                  );
                })}
                {(autoProductsData?.products ?? []).length === 0 && (
                  <span className="text-xs text-text-muted">{t('common.noResults')}</span>
                )}
              </div>
            </div>
          )}

          <Select
            label={t('automation.action', 'Then (set delivery status to)')}
            options={[
              { value: 'confirming', label: t('automation.confirming', 'Awaiting confirmation') },
              { value: 'confirmed', label: t('automation.confirmed', 'Confirmed') },
              { value: 'delivering', label: t('automation.delivering', 'Delivering') },
              { value: 'delivered', label: t('automation.delivered', 'Delivered') },
            ]}
            value={newRule.actionValue}
            onChange={(e) => setNewRule({ ...newRule, actionValue: e.target.value })}
          />

          <Input
            label={t('automation.priorityLabel', 'Priority (higher runs first)')}
            type="number"
            min={0}
            value={String(newRule.priority)}
            onChange={(e) => setNewRule({ ...newRule, priority: Number(e.target.value) || 0 })}
          />
        </div>
      </Modal>

      {/* Bulk Label Creation Modal */}
      <Modal
        isOpen={showBulkLabelModal}
        onClose={() => !bulkLabelProgress && setShowBulkLabelModal(false)}
        title={bulkLabelStep === 1
          ? t('orders.bulkLabels', 'Shipping Labels')
          : t('orders.createLabels', 'Create Shipping Labels')}
        size="lg"
        footer={
          bulkLabelStep === 1 ? (
            <>
              <Button variant="secondary" onClick={() => setShowBulkLabelModal(false)}>
                {t('common.cancel')}
              </Button>
              {bulkLabelOrders.withLabels.length > 0 && (
                <Button variant="secondary" onClick={handleBulkDownloadReadyOnly}>
                  <Download className="h-4 w-4" />
                  {t('orders.downloadReadyOnly', 'Download Ready Only')} ({bulkLabelOrders.withLabels.length})
                </Button>
              )}
              <Button onClick={handleBulkCreateMissing}>
                <Truck className="h-4 w-4" />
                {t('orders.createMissingAndDownload', 'Create Missing & Download All')}
              </Button>
            </>
          ) : bulkLabelStep === 2 && !bulkLabelProgress ? (
            <>
              <Button variant="secondary" onClick={() => setBulkLabelStep(1)}>
                {t('common.back', 'Back')}
              </Button>
              <Button
                onClick={handleBulkCreateLabels}
                disabled={!bulkCarrier || bulkCarriersLoading}
              >
                <Truck className="h-4 w-4" />
                {t('orders.createLabels', 'Create Labels')} ({bulkLabelOrders.withoutLabels.length})
              </Button>
            </>
          ) : null
        }
      >
        {/* Step 1: Summary */}
        {bulkLabelStep === 1 && (
          <div className="space-y-4">
            {/* Summary counts */}
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="flex items-center gap-3 rounded-lg border border-border-default bg-bg-tertiary p-4">
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-success/15">
                  <CheckCircle className="h-5 w-5 text-success" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{bulkLabelOrders.withLabels.length}</p>
                  <p className="text-xs text-text-muted">{t('orders.readyToDownload', 'Ready to download')}</p>
                </div>
              </div>
              <div className="flex items-center gap-3 rounded-lg border border-warning/30 bg-warning/5 p-4">
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-warning/15">
                  <AlertTriangle className="h-5 w-5 text-warning" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{bulkLabelOrders.withoutLabels.length}</p>
                  <p className="text-xs text-text-muted">{t('orders.missingLabels', 'Missing labels')}</p>
                </div>
              </div>
            </div>

            <p className="text-sm text-text-secondary">
              {t('orders.bulkLabelSummary',
                `${bulkLabelOrders.withLabels.length} of ${bulkLabelOrders.total} selected orders have shipping labels. ${bulkLabelOrders.withoutLabels.length} orders need labels created.`
              )}
            </p>

            {/* List of orders without labels */}
            {bulkLabelOrders.withoutLabels.length > 0 && (
              <div className="rounded-lg border border-border-default bg-bg-tertiary/50">
                <div className="px-4 py-2 border-b border-border-default">
                  <p className="text-xs font-medium text-text-muted">
                    {t('orders.ordersWithoutLabels', 'Orders without labels')}
                  </p>
                </div>
                <div className="max-h-40 overflow-y-auto divide-y divide-border-default/50">
                  {bulkLabelOrders.withoutLabels.map((order: any) => (
                    <div key={order._id} className="flex items-center justify-between px-4 py-2">
                      <span className="text-sm font-mono text-accent">
                        #{order.orderNumber ?? order._id.slice(-6)}
                      </span>
                      <span className="text-xs text-text-muted">
                        {order.customer?.name ?? '-'}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Step 2: Carrier selection & label creation */}
        {bulkLabelStep === 2 && !bulkLabelProgress && (
          <div className="space-y-4">
            <Input
              label={t('orders.defaultWeight', 'Default Weight (kg)')}
              type="number"
              step="0.1"
              min="0.1"
              value={bulkWeight}
              onChange={(e) => setBulkWeight(e.target.value)}
              placeholder="1.0"
            />
            <p className="text-xs text-text-muted">
              {t('orders.weightHint', 'This weight will be applied to all orders that need labels created.')}
            </p>

            <div>
              <label className="mb-2 block text-sm font-medium">
                {t('orders.selectCarrier', 'Select Carrier')}
              </label>
              {bulkCarriersLoading ? (
                <div className="flex items-center justify-center py-8">
                  <LoadingSpinner />
                </div>
              ) : bulkCarriers.length === 0 ? (
                <p className="text-sm text-text-muted text-center py-6">
                  {t('orders.noCarriers', 'No shipping companies available')}
                </p>
              ) : (
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {bulkCarriers.map((carrier) => (
                    <button
                      key={carrier.id}
                      type="button"
                      onClick={() => setBulkCarrier(carrier.id)}
                      className={`flex w-full items-center gap-4 rounded-lg border p-4 text-start transition-colors ${
                        bulkCarrier === carrier.id
                          ? 'border-accent bg-accent/5'
                          : 'border-border-default hover:border-accent/50 hover:bg-bg-hover'
                      }`}
                    >
                      {carrier.carrier_logo ? (
                        <img src={carrier.carrier_logo} alt="" className="h-10 w-10 rounded-lg object-contain" />
                      ) : (
                        <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-bg-tertiary">
                          <Truck className="h-5 w-5 text-text-muted" />
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium">{carrier.title_arabic || carrier.title}</p>
                        {carrier.approximate_delivery_time_arabic && (
                          <p className="text-xs text-text-muted">{carrier.approximate_delivery_time_arabic}</p>
                        )}
                        {carrier.warning && (
                          <p className="text-xs text-warning mt-0.5">{carrier.warning}</p>
                        )}
                      </div>
                      {carrier.rate != null && (
                        <div className="text-end shrink-0">
                          <CurrencyDisplay amount={carrier.rate} currency={currentStore?.currency} className="font-semibold" />
                        </div>
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Progress indicator */}
        {bulkLabelProgress && (
          <div className="space-y-4 py-4">
            <div className="flex items-center justify-center">
              <LoadingSpinner size="lg" />
            </div>
            <div className="text-center">
              <p className="text-sm font-medium">
                {t('orders.creatingLabel', 'Creating label {{current}} of {{total}}...', {
                  current: bulkLabelProgress.current,
                  total: bulkLabelProgress.total,
                })}
              </p>
              <p className="mt-1 text-xs text-text-muted">
                {t('orders.pleaseWait', 'Please wait, do not close this window.')}
              </p>
            </div>
            {/* Progress bar */}
            <div className="mx-auto w-full max-w-xs">
              <div className="h-2 rounded-full bg-bg-tertiary overflow-hidden">
                <div
                  className="h-full rounded-full bg-accent transition-all duration-300"
                  style={{ width: `${(bulkLabelProgress.current / bulkLabelProgress.total) * 100}%` }}
                />
              </div>
              <p className="mt-1 text-center text-xs text-text-muted">
                {Math.round((bulkLabelProgress.current / bulkLabelProgress.total) * 100)}%
              </p>
            </div>
          </div>
        )}
      </Modal>

      {/* Quotation Modal */}
      <QuotationModal
        isOpen={showQuotation}
        onClose={() => setShowQuotation(false)}
        storeId={storeId!}
      />

      {/* Order overlays - replaces page navigation */}
      <OrderDetailsSlideOver
        isOpen={!!viewOrderId}
        onClose={() => {
          setViewOrderId(null);
          // Backend marks the order seen on GET /:orderId, but the cached
          // orders list still has seen:false. Refetch so the "New" badge
          // and unseen counter reflect reality.
          queryClient.invalidateQueries({ queryKey: ['orders', storeId] });
          queryClient.invalidateQueries({ queryKey: ['orders-unseen', storeId] });
          queryClient.invalidateQueries({ queryKey: ['orders-status-counts', storeId] });
        }}
        orderId={viewOrderId}
        storeId={storeId!}
      />
      <OrderCreateModal
        isOpen={showCreateOrder}
        onClose={() => setShowCreateOrder(false)}
        storeId={storeId!}
      />
      <CreateShippingLabelModal
        isOpen={!!labelOrderId}
        onClose={() => setLabelOrderId(null)}
        orderId={labelOrderId}
        storeId={storeId!}
      />
    </div>
  );
}
